import { Settings, DateTime } from 'luxon';
import store from '@/store';
import {
  formats,
  locales,
  timezones,
} from '@/constants/localizer';

const DEFAULT_FORMAT = formats.DATETIME_SHORT_WITH_SECONDS;
const OPERATIONS = {
  MORE: 'more',
  LESS: 'less',
  EQUAL: 'equal',
  DIFFERENT: 'different',
  MORE_OR_EQUAL: 'more-or-equal',
  LESS_OR_EQUAL: 'less-or-equal',
};
const SYSTEM_INITIAL_DATE = '1950-01-01T00:00:00.000';
const SYSTEM_FINAL_DATE = '2500-01-01T00:00:00.000';
const GET_USER_LANGUAGE = () => store?.getters?.language?.key || locales.PT_BR;
const GET_USER_TIME_ZONE = () => store.getters.ianaZone;

function assignDefaultLocale(language = GET_USER_LANGUAGE()) {
  Settings.defaultLocale = language;
}

/**
 * NÃO ALTERAR
 * Dada as situações em que o front recebe da API datas no formato ISO sem a identificação do
 * timezone, é necessário que a lib do luxon seja configurada com o timezone UTC por padrão,
 * a fim de tratar estas datas sempre como UTC.
 * Todas as datas enviadas para a API, deverão estar no tmz UTC e as datas fornecidas
 * para o usuário deverão ser apresentadas no fuso horário configurado no cadastro do mesmo.
 */
Settings.defaultZone = timezones.UTC;

assignDefaultLocale();

/**
 * Converte data/hora no formato ISO para data/hora no formato luxon.
 * @param {String} dateTimeISO Valor data/hora em ISO.
 * @returns {Date|luxon} Valor data/hora no formato Luxon.
 */
function convertISOToLuxon(dateTimeISO) {
  return DateTime.fromISO(dateTimeISO);
}

/**
 * Formatador de data com suporte a tokens ou quaisquer opções de objeto suportado por Intl
 * DateTimeFormat.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @param {Object|String} format Formato a ser considerado.
 * @param {String} timeZone Fuso horário desejado.
 * @param {*} fallback Valor a ser retornado caso ocorra alguma falha.
 * @returns {String} Data formatada no formato e fuso horário definido.
 */
function formatter(
  dateTimeISO,
  format = DEFAULT_FORMAT,
  timeZone,
  fallback = null,
) {
  if (!dateTimeISO) {
    return fallback;
  }

  let dateTimeLuxon = convertISOToLuxon(dateTimeISO);

  if (!dateTimeLuxon.isValid) {
    return fallback;
  }

  dateTimeLuxon = dateTimeLuxon.setZone(timeZone || GET_USER_TIME_ZONE());

  if (typeof (format) === 'object') {
    return dateTimeLuxon.toLocaleString(format);
  }

  return dateTimeLuxon.toFormat(format);
}

/**
 * Transforma uma data formatada em uma data e hora no formato ISO.
 * Será considerado o fuso do usuário logado para a data de entrada.
 * @param {String} dateTimeText Data formatada.
 * @param {String} format Formato da data.
 * @param {*} opts Objeto com configurações de data a ser considerada durante a conversão.
 * @returns Data e hora no formato ISO em UTC.
 */
function fromFormat(dateTimeText, format, opts = {}) {
  const luxonFromFormatOptions = {
    zone: opts?.timeZone || GET_USER_TIME_ZONE(),
  };
  return DateTime
    .fromFormat(dateTimeText, format, luxonFromFormatOptions)
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Executa a operação desejada entre duas datas no formato ISO.
 * @param {String} firstDateTimeISO Valor data e hora em formato ISO a ser comparado.
 * @param {String} secondDateTimeISO Valor data e hora em formato ISO a ser comparado.
 * @param {String|OPERATIONS} [operation=-1] Operação a ser executada.
 * @returns Resultado da operação executada.
 */
function getOperationsResult(firstDateTimeISO, secondDateTimeISO, operation = -1) {
  const luxonFirstDatetime = convertISOToLuxon((firstDateTimeISO));
  const luxonSecondDatetime = convertISOToLuxon((secondDateTimeISO));

  if (operation === -1) return false;
  if (!luxonFirstDatetime || !luxonFirstDatetime.isValid) return false;
  if (!luxonSecondDatetime || !luxonSecondDatetime.isValid) return false;

  switch (operation) {
    case OPERATIONS.LESS:
      return luxonFirstDatetime < luxonSecondDatetime;
    case OPERATIONS.MORE:
      return luxonFirstDatetime > luxonSecondDatetime;
    case OPERATIONS.LESS_OR_EQUAL:
      return luxonFirstDatetime <= luxonSecondDatetime;
    case OPERATIONS.MORE_OR_EQUAL:
      return luxonFirstDatetime >= luxonSecondDatetime;
    case OPERATIONS.EQUAL:
      return +luxonFirstDatetime === +luxonSecondDatetime;
    case OPERATIONS.DIFFERENT:
      return +luxonFirstDatetime !== +luxonSecondDatetime;
    default:
      return false;
  }
}

/**
 * Obtêm a data e hora atual no formato ISO em UTC.
 * @returns Retorna a data e hora atual no formato ISO em UTC.
 */
function getNow() {
  return DateTime
    .now()
    .toISO();
}

/**
 * Converte uma data e hora no formato ISO para o fuso horário a ser definido.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @param {String} [timeZone=USER_TIME_ZONE] Fuso horário desejado.
 * @returns Retorna a data e hora no formato ISO com o fuso horário definido.
 */
function toTimeZone(dateTimeISO, timeZone) {
  return DateTime
    .fromISO(dateTimeISO)
    .setZone(timeZone || GET_USER_TIME_ZONE())
    .toISO();
}

/**
 * Indica se a primeira data é menor que a segunda.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @returns Indicativo se primeira data é menor que a segunda.
 */
function isLess(firstDateTimeISO, secondDateTimeISO) {
  return getOperationsResult(firstDateTimeISO, secondDateTimeISO, OPERATIONS.LESS);
}

/**
 * Indica se a primeira data é maior que a segunda.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @returns Indicativo se primeira data é maior que a segunda.
 */
function isGreater(firstDateTimeISO, secondDateTimeISO) {
  return getOperationsResult(firstDateTimeISO, secondDateTimeISO, OPERATIONS.MORE);
}

/**
 * Indica se a primeira data é menor ou igual que a segunda.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @returns Indicativo se primeira data é menor ou igual que a segunda.
 */
function isLessOrEqual(firstDateTimeISO, secondDateTimeISO) {
  return getOperationsResult(firstDateTimeISO, secondDateTimeISO, OPERATIONS.LESS_OR_EQUAL);
}

/**
 * Indica se a primeira data é maior ou igual que a segunda.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @returns Indicativo se primeira data é maior ou igual que a segunda.
 */
function isGreaterOrEqual(firstDateTimeISO, secondDateTimeISO) {
  return getOperationsResult(firstDateTimeISO, secondDateTimeISO, OPERATIONS.MORE_OR_EQUAL);
}

/**
 * Indica se as duas data são iguais.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @returns Indicativo da igualdade entre as duas datas.
 */
function isEqual(firstDateTimeISO, secondDateTimeISO) {
  return getOperationsResult(firstDateTimeISO, secondDateTimeISO, OPERATIONS.EQUAL);
}

/**
 * Indica se as duas datas são diferentes.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @returns Indicativo da diferença entre as duas datas.
 */
function isDifferent(firstDateTimeISO, secondDateTimeISO) {
  return getOperationsResult(firstDateTimeISO, secondDateTimeISO, OPERATIONS.DIFFERENT);
}

/**
 * Retorna uma função para verificar se uma data está entre outras duas datas.
 * @param {String} minDateTimeISO Data e hora no formato ISO.
 * @param {String} maxDateTimeISO Data e hora no formato ISO.
 * @param {Boolean} inclusive Considera a igualdade para a comparação.
 * @returns Retorna uma função para verificar se uma data se encontra entre duas outras datas.
 */
function isBetween(minDateTimeISO, maxDateTimeISO, inclusive = true) {
  if (inclusive) {
    return value => isLessOrEqual(minDateTimeISO, value)
      && isGreaterOrEqual(maxDateTimeISO, value);
  }

  return value => isLess(minDateTimeISO, value)
    && isGreater(maxDateTimeISO, value);
}

/**
 * Soma no parâmetro de data e hora as opções disponíveis desejadas.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @param {Object} options Objeto com as opções que serão usadas na operação de soma.
 * @returns Data e hora no formato ISO.
 */
function plus(dateTimeISO, options = {
  years: 0,
  months: 0,
  days: 0,
  quarters: 0,
  weeks: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
}) {
  const luxonDateTime = convertISOToLuxon((dateTimeISO));
  return luxonDateTime
    .plus(options)
    .toISO();
}

/**
 * Subtrai no parâmetro de data e hora as opções disponíveis desejadas.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @param {Object} options Objeto com as opções que serão usadas na operação de subtração.
 * @returns Data e hora no formato ISO.
 */
function minus(dateTimeISO, options = {
  years: 0,
  months: 0,
  days: 0,
  quarters: 0,
  weeks: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
}) {
  const luxonDateTime = convertISOToLuxon((dateTimeISO));
  return luxonDateTime
    .minus(options)
    .toISO();
}

/**
 * Atribui o tempo em uma data e hora no formato ISO fornecido.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @param {String} timeString Hora no formato HH:mm:ss.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Data e hora no formato ISO resultante da junção dos parâmetros fornecidos.
 */
function joinISODateAndTimeString(dateTimeISO, timeString, opts = {}) {
  const timeParts = (timeString || '').split(':');
  const luxonDatetime = convertISOToLuxon(dateTimeISO);
  return luxonDatetime
    .setZone(opts?.timeZone || GET_USER_TIME_ZONE())
    .set({
      hour: parseInt(timeParts[0] || '0', 10),
      minute: parseInt(timeParts[1] || '0', 10),
      second: parseInt(timeParts[2] || '0', 10),
    })
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Obtêm os milissegundos de uma data e hora.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @returns Milissegundos de uma data e hora.
 */
function toMilliseconds(dateTimeISO) {
  const luxonDatetime = convertISOToLuxon(dateTimeISO);
  return luxonDatetime.toMillis();
}

/**
 * Verificar se uma data e hora é válida.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @returns Indicativo se data e hora é válida.
 */
function isValid(dateTimeISO) {
  return convertISOToLuxon(dateTimeISO)
    .isValid;
}

/**
 * Extrai a informação do dia de uma data
 * @param {*} dateTimeISO Data e hora no formato ISO.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Valor do dia.
 */
function getDay(dateTimeISO, opts = {}) {
  let luxonDatetime = convertISOToLuxon(dateTimeISO);
  luxonDatetime = luxonDatetime.setZone(opts?.timeZone || GET_USER_TIME_ZONE());
  return luxonDatetime.day;
}

/**
 * Extrai a informação do mês de uma data
 * @param {*} dateTimeISO Data e hora no formato ISO.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Valor do mês.
 */
function getMonth(dateTimeISO, opts = {}) {
  let luxonDatetime = convertISOToLuxon(dateTimeISO);
  luxonDatetime = luxonDatetime.setZone(opts?.timeZone || GET_USER_TIME_ZONE());
  return luxonDatetime.month;
}

/**
 * Extrai a informação do ano de uma data
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Valor do ano.
 */
function getYear(dateTimeISO, opts = {}) {
  let luxonDatetime = convertISOToLuxon(dateTimeISO);
  luxonDatetime = luxonDatetime.setZone(opts?.timeZone || GET_USER_TIME_ZONE());
  return luxonDatetime.year;
}

/**
 * Obtêm a data e hora de ínicio do dia atual com base no tmz do usuário.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Retorna data e hora no formato ISO em UTC.
 */
function getStartOfDay(opts = {}) {
  return DateTime
    .now()
    .setZone(opts.timeZone || GET_USER_TIME_ZONE())
    .startOf('day')
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Obtêm a data e hora de encerramento do dia atual com base no tmz do usuário.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Returna data e hora no formato ISO em UTC.
 */
function getEndOfDay(opts = {}) {
  return DateTime
    .now()
    .setZone(opts.timeZone || GET_USER_TIME_ZONE())
    .endOf('day')
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Obtêm a data e hora inicial do sistema com base no tmz do usuário.
 * @returns Retorna data e hora no formato ISO em UTC.
 */
function getInitialDate(opts = {}) {
  return DateTime
    .fromISO(SYSTEM_INITIAL_DATE, { zone: opts.timeZone || GET_USER_TIME_ZONE() })
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Normaliza uma data para o início do dia no fuso do usuário (em UTC).
 * @param {String} dateTimeISO Data e hora no formato ISO (pode ser apenas data, ex: 1950-01-01).
 * @returns Data e hora do início do dia no fuso do usuário, em ISO UTC.
 */
function normalizeToStartOfDayInUserZone(dateTimeISO) {
  if (!dateTimeISO) return dateTimeISO;
  const dateOnly = typeof dateTimeISO === 'string' && dateTimeISO.includes('T')
    ? dateTimeISO.slice(0, 10)
    : dateTimeISO;
  const luxon = DateTime.fromISO(dateOnly, { zone: GET_USER_TIME_ZONE() });
  if (!luxon.isValid) return dateTimeISO;
  return luxon.startOf('day').setZone(timezones.UTC).toISO();
}

/**
 * Obtêm a data e hora final do sistema com base no tmz do usuário.
 * @returns Retorna data e hora no formato ISO em UTC.
 */
function getFinalDate(opts = {}) {
  return DateTime
  .fromISO(SYSTEM_FINAL_DATE, { zone: opts.timeZone || GET_USER_TIME_ZONE() })
  .setZone(timezones.UTC)
  .toISO();
}

/**
 * Modifica o tempo da data para o ínicio do dia.
 * @param {String} dateTimeISO Data e hora no formato ISO a ser modificada.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Retorna data e hora no formato ISO em UTC.
 */
function setToStartOfDay(dateTimeISO, opts = {}) {
  return convertISOToLuxon(dateTimeISO)
    .setZone(opts?.timeZone || GET_USER_TIME_ZONE())
    .startOf('day')
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Modifica o tempo da data para o fim do dia.
 * @param {String} dateTimeISO Data e hora no formato ISO a ser modificada.
 * @param {*} opts Opções de configuração da data e hora a ser considerada na conversão.
 * @returns Retorna data e hora no formato ISO em UTC.
 */
function setToEndOfDay(dateTimeISO, opts = {}) {
  return convertISOToLuxon(dateTimeISO)
    .setZone(opts?.timeZone || GET_USER_TIME_ZONE())
    .endOf('day')
    .setZone(timezones.UTC)
    .toISO();
}

/**
 * Retorna informações da diferença entre duas datas.
 * @param {String} firstDateTimeISO Data e hora no formato ISO.
 * @param {String} secondDateTimeISO Data e hora no formato ISO.
 * @param {String|Array[String]} unit Unidades de medida do tipo data a serem consideradas para
 * calcular a diferença entre as datas.
 * @returns Informativo com base nas unidades fornecidades da diferença das duas datas.
 */
function showDiff(firstDateTimeISO, secondDateTimeISO, unit) {
  const luxonInitialDate = convertISOToLuxon(firstDateTimeISO);
  const luxonFinalDate = convertISOToLuxon(secondDateTimeISO);
  return luxonInitialDate.diff(luxonFinalDate, unit).toObject();
}

/**
 * Remove da data e hora ISO a informação do fuso horário.
 * @param {String} dateTimeISO Data e hora no formato ISO.
 * @returns Data e hora no formato ISO sem o indicativo do fuso horário.
 */
function supressOffset(dateTimeISO) {
  return convertISOToLuxon(dateTimeISO)
    .toISO({ includeOffset: false });
}

export {
  assignDefaultLocale,
  formatter,
  fromFormat,
  getEndOfDay,
  getFinalDate,
  getInitialDate,
  getNow,
  getStartOfDay,
  getDay,
  getMonth,
  getYear,
  isBetween,
  isDifferent,
  isEqual,
  isGreater,
  isGreaterOrEqual,
  isLess,
  isLessOrEqual,
  isValid,
  joinISODateAndTimeString,
  minus,
  normalizeToStartOfDayInUserZone,
  plus,
  setToEndOfDay,
  setToStartOfDay,
  showDiff,
  supressOffset,
  toMilliseconds,
  toTimeZone,
};
